/**
 * CGU des clients — console « CGU clients » (migration 0052), RÉSERVÉE AUX ADMINS.
 *
 * Les versions sont suivies EN INTERNE : l'application n'affiche jamais de numéro ni d'historique. Ici, on édite
 * le brouillon, on publie (l'ancienne version sort de la vigueur mais reste lisible, en lecture seule) et on
 * compte les acceptations par version. Les règles (immuabilité, une seule version en vigueur, texte provisoire
 * non publiable, empreinte) sont DANS LA BASE ; ce module ne les rejoue pas, il les rend lisibles.
 */
import { traduireErreurOnboarding } from '../onboarding/erreurs'
import { supabase } from './supabase'

export interface VersionCgu {
  id: string
  /** Identifiant interne, jamais montré aux clients : « swifteats-cgu-v2 ». */
  version: string
  corps: string
  provisoire: boolean
  publieeLe: string | null
  enVigueur: boolean
  creeLe: string
}

export interface DecompteCgu {
  versionId: string
  nbAcceptations: number
  derniereAcceptation: string | null
  /** Clients enregistrés (même valeur sur chaque ligne) : sert de dénominateur. */
  nbClients: number
}

export type EtatVersionCgu = 'brouillon' | 'en_vigueur' | 'archivee'

export const PREFIXE_VERSION_CGU = 'swifteats-cgu-v'

export function etatVersion(v: Pick<VersionCgu, 'publieeLe' | 'enVigueur'>): EtatVersionCgu {
  if (v.publieeLe === null) return 'brouillon'
  return v.enVigueur ? 'en_vigueur' : 'archivee'
}

export const LIBELLE_ETAT_CGU: Record<EtatVersionCgu, string> = {
  brouillon: 'Brouillon',
  en_vigueur: 'En vigueur',
  archivee: 'Archivée',
}

/** « swifteats-cgu-v7 » : le numéro suivant le plus grand déjà pris (les versions mal formées sont ignorées). */
export function prochainIdentifiantVersion(existantes: readonly string[]): string {
  let max = 0
  for (const v of existantes) {
    const m = /^swifteats-cgu-v(\d+)$/.exec(v)
    if (m) max = Math.max(max, Number(m[1]))
  }
  return `${PREFIXE_VERSION_CGU}${max + 1}`
}

export interface ControleTexteCgu {
  vide: boolean
  /** Passages « [ … ] » restés dans le texte : des trous à compléter avant publication. */
  crochets: string[]
}

/** Les liens markdown `[texte](url)` ne comptent pas comme des trous. */
export function controlerTexteCgu(corps: string): ControleTexteCgu {
  const crochets: string[] = []
  for (const m of corps.matchAll(/\[([^\]\n]*)\](?!\()/g)) crochets.push(`[${m[1]}]`)
  return { vide: corps.trim() === '', crochets: [...new Set(crochets)] }
}

/**
 * Pourquoi la publication est impossible (phrase), ou null si elle est permise. Les mêmes refus existent dans la
 * base ; les dire ici évite un aller-retour et une erreur technique.
 */
export function obstaclePublication(
  v: Pick<VersionCgu, 'provisoire' | 'publieeLe'>,
  corps: string,
  provisoire: boolean,
  enVigueur: Pick<VersionCgu, 'corps'> | null,
): string | null {
  if (v.publieeLe !== null) return 'Cette version est déjà publiée.'
  if (controlerTexteCgu(corps).vide) return 'Le texte est vide.'
  if (provisoire) {
    return 'Ce texte est marqué « provisoire » : faites-le relire par le juriste, puis décochez la case avant de publier.'
  }
  if (enVigueur && enVigueur.corps === corps) return 'Ce texte est identique à celui qui est déjà en vigueur.'
  return null
}

/** « 12 clients sur 340 (4 %) » ; « Aucun client enregistré » ; jamais de division par zéro. */
export function libelleAcceptations(nb: number, total: number): string {
  if (total <= 0) return nb > 0 ? `${nb} acceptation${nb > 1 ? 's' : ''}` : 'Aucun client enregistré'
  const part = Math.min(100, Math.round((nb / total) * 100))
  return `${nb} client${nb > 1 ? 's' : ''} sur ${total} (${part} %)`
}

// ── Base ─────────────────────────────────────────────────────────────────

interface LigneVersion {
  id: string
  version: string
  corps_md: string
  provisoire: boolean
  publiee_le: string | null
  en_vigueur: boolean
  created_at: string
}

const COLONNES = 'id,version,corps_md,provisoire,publiee_le,en_vigueur,created_at'

const versVersion = (l: LigneVersion): VersionCgu => ({
  id: l.id,
  version: l.version,
  corps: l.corps_md,
  provisoire: l.provisoire,
  publieeLe: l.publiee_le,
  enVigueur: l.en_vigueur,
  creeLe: l.created_at,
})

function lever(error: { message?: string } | null): void {
  if (error) throw new Error(traduireErreurOnboarding(error.message))
}

/** Une mise à jour qui ne touche aucune ligne = droit manquant (RLS) ou version déjà publiée. */
function exigerLigne(data: unknown[] | null, quoi: string): void {
  if (!data || data.length === 0) {
    throw new Error(`${quoi} : aucune ligne modifiée (réservé aux administrateurs, ou version déjà publiée).`)
  }
}

/** Plus récente d'abord. */
export async function listerVersionsCgu(): Promise<VersionCgu[]> {
  const { data, error } = await supabase
    .from('cgu_versions')
    .select(COLONNES)
    .eq('produit', 'swifteats')
    .order('created_at', { ascending: false })
    .order('version', { ascending: false })
  lever(error)
  return ((data as LigneVersion[] | null) ?? []).map(versVersion)
}

interface LigneDecompte {
  version_id: string
  nb_acceptations: number | string
  derniere_acceptation: string | null
  nb_clients: number | string
}

export async function listerDecomptesCgu(): Promise<DecompteCgu[]> {
  const { data, error } = await supabase.rpc('cgu_decomptes')
  lever(error)
  return ((data as LigneDecompte[] | null) ?? []).map((l) => ({
    versionId: l.version_id,
    // bigint : PostgREST le rend en nombre, certains pilotes en texte.
    nbAcceptations: Number(l.nb_acceptations) || 0,
    derniereAcceptation: l.derniere_acceptation,
    nbClients: Number(l.nb_clients) || 0,
  }))
}

export interface ChampsCgu {
  corps: string
  provisoire: boolean
}

/** Nouveau brouillon : numéro suivant, texte de départ = celui qu'on donne (en général la version en vigueur). */
export async function creerBrouillonCgu(existantes: readonly string[], c: ChampsCgu): Promise<VersionCgu> {
  const { data, error } = await supabase
    .from('cgu_versions')
    .insert({ version: prochainIdentifiantVersion(existantes), corps_md: c.corps, provisoire: c.provisoire })
    .select(COLONNES)
    .single()
  lever(error)
  return versVersion(data as LigneVersion)
}

export async function enregistrerBrouillonCgu(id: string, c: ChampsCgu): Promise<void> {
  const { data, error } = await supabase
    .from('cgu_versions')
    .update({ corps_md: c.corps, provisoire: c.provisoire })
    .eq('id', id)
    .select('id')
  lever(error)
  exigerLigne(data, 'Enregistrement du brouillon')
}

export async function supprimerBrouillonCgu(id: string): Promise<void> {
  const { data, error } = await supabase.from('cgu_versions').delete().eq('id', id).select('id')
  lever(error)
  exigerLigne(data, 'Suppression du brouillon')
}

/**
 * Publie un brouillon (ses dernières modifications d'abord, puis la fonction `publier_cgu`). La base refuse un texte
 * provisoire ou identique à la version en vigueur, retire l'ancienne version de la vigueur et pose l'heure ellemême.
 */
export async function publierCgu(id: string, c: ChampsCgu): Promise<void> {
  await enregistrerBrouillonCgu(id, c)
  const { error } = await supabase.rpc('publier_cgu', { p_id: id })
  lever(error)
}
